import React from 'react';

export type NoticeTone = 'success' | 'error' | 'warning' | 'info';

export function ActionButton({
  pending = false,
  pendingLabel = '处理中…',
  disabledReason,
  children,
  disabled,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  pending?: boolean;
  pendingLabel?: string;
  disabledReason?: string;
}) {
  const blocked = Boolean(disabled || pending || disabledReason);
  return (
    <span className="admin-action-control">
      <button {...props} onClick={onClick} disabled={blocked} aria-busy={pending || undefined}>
        {pending ? pendingLabel : children}
      </button>
      {disabledReason ? <DisabledReason>{disabledReason}</DisabledReason> : null}
    </span>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: NoticeTone;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`admin-notice admin-notice--${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      {children}
    </div>
  );
}

export function FieldError({ id, children }: { id?: string; children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <small id={id} className="admin-field-error" role="alert">
      {children}
    </small>
  );
}

export function DisabledReason({ children }: { children: React.ReactNode }) {
  return (
    <small className="admin-disabled-reason" role="note">
      {children}
    </small>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = '确认',
  cancelLabel = '取消',
  dangerous = false,
  pending = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  dangerous?: boolean;
  pending?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="admin-dialog-backdrop" role="presentation" onMouseDown={onCancel}>
      <section
        className="admin-confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="admin-confirm-dialog-title"
        aria-describedby="admin-confirm-dialog-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 id="admin-confirm-dialog-title">{title}</h2>
        <div id="admin-confirm-dialog-description">{description}</div>
        <div className="admin-confirm-dialog__actions">
          <button type="button" disabled={pending} onClick={onCancel}>
            {cancelLabel}
          </button>
          <ActionButton
            type="button"
            className={dangerous ? 'danger' : 'primary'}
            pending={pending}
            disabled={confirmDisabled}
            pendingLabel="处理中…"
            onClick={onConfirm}
          >
            {confirmLabel}
          </ActionButton>
        </div>
      </section>
    </div>
  );
}
